import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, Check, ChevronDown, Layers, Plus } from 'lucide-react';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useAltitudeText } from '../../hooks/prioritization/useAltitudeText';
import type { Item, ItemAltitude } from '../../types/prioritization';

// Altitude hierarchy for filtering (higher index = higher level)
const ALTITUDE_ORDER: ItemAltitude[] = ['task', 'story', 'feature', 'epic', 'initiative', 'outcome'];

interface SearchableItemPickerProps {
  /** Currently selected item ID */
  value: string | undefined;
  /** Callback when selection changes */
  onChange: (itemId: string | undefined) => void;
  /** ID of the current item (to exclude from list) */
  excludeId?: string;
  /** IDs to exclude from list (use when multiple items need to be excluded) */
  excludeIds?: string[];
  /** Label for the picker */
  label: string;
  /** Helper text below the label */
  helperText?: string;
  /** Placeholder when no item selected */
  placeholder?: string;
  /** Only show items at or above this altitude */
  minAltitude?: ItemAltitude;
  /** Only show items at or below this altitude */
  maxAltitude?: ItemAltitude;
  /** Only show items with these lifecycle states */
  lifecycleStates?: ('item')[];
  /** Whether this field is required */
  required?: boolean;
  /** Additional filter function */
  filterFn?: (item: Item) => boolean;
  /** Show "No selection" option */
  allowEmpty?: boolean;
  /** Empty state label */
  emptyLabel?: string;
  /** Allow creating a new item inline from the search query */
  allowCreate?: boolean;
  /** Label shown on the create option, defaults to 'New item' */
  createLabel?: string;
}

export function SearchableItemPicker({
  value,
  onChange,
  excludeId,
  excludeIds,
  label,
  helperText,
  placeholder = 'Search items...',
  minAltitude,
  maxAltitude,
  lifecycleStates = ['item'],
  required = false,
  filterFn,
  allowEmpty = true,
  emptyLabel = 'No selection',
  allowCreate = false,
  createLabel = 'New item',
}: SearchableItemPickerProps) {
  const altitudeText = useAltitudeText();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const items = useItemsStore((state) => state.items);
  const getItemById = useItemsStore((state) => state.getItemById);
  const createItem = useItemsStore((state) => state.createItem);
  
  // Get selected item details
  const selectedItem = value ? getItemById(value) : undefined;
  
  // Filter and search items
  const filteredItems = useMemo(() => {
    let filtered = items;
    
    // Exclude current item
    if (excludeId) {
      filtered = filtered.filter(c => c.id !== excludeId);
    }
    if (excludeIds && excludeIds.length > 0) {
      filtered = filtered.filter(c => !excludeIds.includes(c.id));
    }
    
    // Filter by lifecycle state (support both snake_case and camelCase from DB/merge)
    if (lifecycleStates.length > 0) {
      filtered = filtered.filter(c => {
        const state = c.lifecycle_state ?? (c as { lifecycleState?: string }).lifecycleState;
        return state ? lifecycleStates.includes(state) : false;
      });
    }
    
    // Filter by altitude range
    if (minAltitude) {
      const minIndex = ALTITUDE_ORDER.indexOf(minAltitude);
      filtered = filtered.filter(c => {
        if (!c.altitude) return true; // Include items without altitude set
        const itemIndex = ALTITUDE_ORDER.indexOf(c.altitude);
        return itemIndex >= minIndex;
      });
    }
    
    if (maxAltitude) {
      const maxIndex = ALTITUDE_ORDER.indexOf(maxAltitude);
      filtered = filtered.filter(c => {
        if (!c.altitude) return true;
        const itemIndex = ALTITUDE_ORDER.indexOf(c.altitude);
        return itemIndex <= maxIndex;
      });
    }
    
    // Apply custom filter
    if (filterFn) {
      filtered = filtered.filter(filterFn);
    }
    
    // Search by title and description
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.title.toLowerCase().includes(query) ||
        (c.description && c.description.toLowerCase().includes(query))
      );
    }
    
    // Sort by relevance (exact title match first, then alphabetical)
    return filtered.sort((a, b) => {
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const aExact = a.title.toLowerCase().startsWith(query);
        const bExact = b.title.toLowerCase().startsWith(query);
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
      }
      return a.title.localeCompare(b.title);
    });
  }, [items, excludeId, excludeIds, lifecycleStates, minAltitude, maxAltitude, filterFn, searchQuery]);
  
  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  
  const handleSelect = (itemId: string | undefined) => {
    onChange(itemId);
    setIsOpen(false);
    setSearchQuery('');
  };
  
  const handleCreateNew = () => {
    const title = searchQuery.trim();
    if (!title || isCreating) return;
    setIsCreating(true);
    const newId = crypto.randomUUID();
    createItem({ id: newId, title, createdBy: 'unknown', tags: [] });
    handleSelect(newId);
    setIsCreating(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };
  
  const getAltitudeColor = (altitude?: ItemAltitude) => {
    switch (altitude) {
      case 'outcome': return 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
      case 'initiative': return 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
      case 'epic': return 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
      case 'feature': return 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]';
      case 'story': return 'bg-[var(--mustard-dim)] text-[var(--mustard)]';
      case 'task': return 'bg-[var(--hover-tint)] text-[var(--text-sec)]';
      default: return 'bg-[var(--hover-tint)] text-[var(--text-ter)]';
    }
  };
  
  return (
    <div ref={containerRef} className="relative">
      {/* Label */}
      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
        {label} {required && <span className="text-[var(--coral)]">*</span>}
      </label>
      
      {helperText && (
        <p className="text-xs text-[var(--text-ter)] mb-2">{helperText}</p>
      )}
      
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 text-left border  flex items-center justify-between transition-colors ${
          isOpen 
            ? 'border-primary-500 ring-2 ring-primary-500/20' 
            : 'border-[var(--rule)] hover:border-[var(--rule)]'
        } ${!selectedItem ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {selectedItem ? (
            <>
              <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ${getAltitudeColor(selectedItem.altitude)}`}>
                {altitudeText.format(selectedItem.altitudeLevel ?? selectedItem.altitude, selectedItem.itemCategory) ?? '?'}
              </span>
              <span className="truncate">{selectedItem.title}</span>
            </>
          ) : (
            <span>{emptyLabel}</span>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 text-[var(--text-ter)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-[var(--rule)]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="w-full pl-9 pr-8 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          
          {/* Options List */}
          <div className="overflow-y-auto max-h-60">
            {/* Empty option */}
            {allowEmpty && (
              <button
                type="button"
                onClick={() => handleSelect(undefined)}
                className={`w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-[var(--surface)] transition-colors ${
                  !value ? 'bg-primary-50' : ''
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  {!value && <Check className="h-4 w-4 text-primary-600" />}
                </div>
                <span className="text-[var(--text-ter)] italic">{emptyLabel}</span>
              </button>
            )}
            
            {/* Candidates */}
            {filteredItems.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-[var(--text-ter)]">
                {searchQuery ? 'No matching items found' : 'No items available'}
              </div>
            ) : (
              filteredItems.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => handleSelect(it.id)}
                  className={`w-full px-3 py-2 text-left flex items-start gap-2 hover:bg-[var(--surface)] transition-colors ${
                    value === it.id ? 'bg-primary-50' : ''
                  }`}
                >
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    {value === it.id && <Check className="h-4 w-4 text-primary-600" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ${getAltitudeColor(it.altitude)}`}>
                        <Layers className="h-3 w-3 mr-0.5" />
                        {altitudeText.format(it.altitudeLevel ?? it.altitude, it.itemCategory) ?? 'unset'}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        (it as { itemPoolId?: string }).itemPoolId
                          ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                          : it.lifecycle_state === 'item'
                          ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                          : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                      }`}>
                        {(it as { itemPoolId?: string }).itemPoolId ? 'Priority' : it.lifecycle_state === 'item' ? 'Item' : 'Intake'}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-[var(--text-pri)] mt-1 truncate">
                      {it.title}
                    </div>
                    {it.description && (
                      <div className="text-xs text-[var(--text-ter)] mt-0.5 truncate">
                        {it.description}
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Create new option */}
          {allowCreate && searchQuery.trim() && (
            <div className="border-t border-[var(--rule)]">
              <button
                type="button"
                onClick={handleCreateNew}
                disabled={isCreating}
                className="w-full px-3 py-2.5 text-left flex items-center gap-2 hover:bg-[var(--slate-dim)] transition-colors text-[var(--text-ter)] disabled:opacity-50"
              >
                <Plus className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium">
                  {isCreating ? 'Creating...' : `${createLabel}: "${searchQuery.trim()}"`}
                </span>
              </button>
            </div>
          )}
          
          {/* Results count */}
          <div className="px-3 py-2 border-t border-[var(--rule)] bg-[var(--surface)] text-xs text-[var(--text-ter)]">
            {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''} 
            {searchQuery && ` matching "${searchQuery}"`}
          </div>
        </div>
      )}
    </div>
  );
}
